import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";

export type SortDirection = "asc" | "desc";

export interface ListState {
    page: number;
    pageSize: number;
    sortBy: string;
    sortDirection: SortDirection;
    search: string;
}

export interface ListStateConfig {
    /** Default sort field */
    defaultSortBy?: string;
    /** Default sort direction */
    defaultSortDirection?: SortDirection;
    /** Default page size */
    defaultPageSize?: number;
    /** URL param prefix to avoid conflicts when multiple lists are on the same page */
    paramPrefix?: string;
    /** Debounce delay for search in ms */
    searchDebounceMs?: number;
}

export interface UseListStateReturn extends ListState {
    /** Set the current page (0-indexed) */
    setPage: (page: number) => void;
    /** Set the page size */
    setPageSize: (size: number) => void;
    /** Set sort field and direction */
    setSort: (field: string, direction?: SortDirection) => void;
    /** Toggle sort direction for a field */
    toggleSort: (field: string) => void;
    /** Set search query (debounced) */
    setSearch: (query: string) => void;
    /** Immediate search value (before debounce) */
    searchInput: string;
    /** Set immediate search input */
    setSearchInput: (query: string) => void;
    /** Clear all filters and reset to defaults */
    reset: () => void;
    /** Check if a column is currently sorted */
    isSorted: (field: string) => boolean;
    /** Get sort direction for a field */
    getSortDirection: (field: string) => SortDirection | undefined;
}

const PAGE_SIZES = [10, 25, 50, 100];

/**
 * Custom hook for managing list state with URL query param persistence.
 * Handles pagination, sorting, and debounced search with URL synchronization.
 * Compatible with React Router v5.
 */
export function useListState(config: ListStateConfig = {}): UseListStateReturn {
    const {
        defaultSortBy = "createdDate",
        defaultSortDirection = "desc",
        defaultPageSize = 10,
        paramPrefix = "",
        searchDebounceMs = 300,
    } = config;

    const location = useLocation();
    const history = useHistory();
    const debounceTimerRef = useRef<NodeJS.Timeout>();

    // Helper to get param name with prefix
    const getParamName = useCallback(
        (name: string) => (paramPrefix ? `${paramPrefix}_${name}` : name),
        [paramPrefix]
    );

    // Parse search params from location
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    // Parse state from URL params
    const stateFromUrl = useMemo((): ListState => {
        const pageParam = searchParams.get(getParamName("page"));
        const sizeParam = searchParams.get(getParamName("size"));
        const sortByParam = searchParams.get(getParamName("sortBy"));
        const sortDirParam = searchParams.get(getParamName("sortDir"));
        const searchParam = searchParams.get(getParamName("search"));

        return {
            page: pageParam ? Math.max(0, parseInt(pageParam, 10) || 0) : 0,
            pageSize: sizeParam
                ? PAGE_SIZES.includes(parseInt(sizeParam, 10))
                    ? parseInt(sizeParam, 10)
                    : defaultPageSize
                : defaultPageSize,
            sortBy: sortByParam || defaultSortBy,
            sortDirection: sortDirParam === "asc" || sortDirParam === "desc" ? sortDirParam : defaultSortDirection,
            search: searchParam || "",
        };
    }, [searchParams, getParamName, defaultSortBy, defaultSortDirection, defaultPageSize]);

    // Local state for immediate search input (before debounce)
    const [searchInput, setSearchInputState] = useState(stateFromUrl.search);

    // Sync search input with URL on mount and URL changes
    useEffect(() => {
        setSearchInputState(stateFromUrl.search);
    }, [stateFromUrl.search]);

    // Update URL params using history.replace (React Router v5)
    const updateParams = useCallback(
        (updates: Partial<ListState>) => {
            const newParams = new URLSearchParams(location.search);

            if (updates.page !== undefined) {
                if (updates.page === 0) {
                    newParams.delete(getParamName("page"));
                } else {
                    newParams.set(getParamName("page"), String(updates.page));
                }
            }

            if (updates.pageSize !== undefined) {
                if (updates.pageSize === defaultPageSize) {
                    newParams.delete(getParamName("size"));
                } else {
                    newParams.set(getParamName("size"), String(updates.pageSize));
                }
            }

            if (updates.sortBy !== undefined) {
                if (updates.sortBy === defaultSortBy) {
                    newParams.delete(getParamName("sortBy"));
                } else {
                    newParams.set(getParamName("sortBy"), updates.sortBy);
                }
            }

            if (updates.sortDirection !== undefined) {
                if (updates.sortDirection === defaultSortDirection) {
                    newParams.delete(getParamName("sortDir"));
                } else {
                    newParams.set(getParamName("sortDir"), updates.sortDirection);
                }
            }

            if (updates.search !== undefined) {
                if (!updates.search) {
                    newParams.delete(getParamName("search"));
                } else {
                    newParams.set(getParamName("search"), updates.search);
                }
            }

            const newSearch = newParams.toString();
            history.replace({
                pathname: location.pathname,
                search: newSearch ? `?${newSearch}` : "",
            });
        },
        [history, location.pathname, location.search, getParamName, defaultPageSize, defaultSortBy, defaultSortDirection]
    );

    const setPage = useCallback(
        (page: number) => {
            updateParams({ page: Math.max(0, page) });
        },
        [updateParams]
    );

    const setPageSize = useCallback(
        (size: number) => {
            const validSize = PAGE_SIZES.includes(size) ? size : defaultPageSize;
            updateParams({ pageSize: validSize, page: 0 }); // Reset to first page on size change
        },
        [updateParams, defaultPageSize]
    );

    const setSort = useCallback(
        (field: string, direction?: SortDirection) => {
            const newDirection = direction || defaultSortDirection;
            updateParams({ sortBy: field, sortDirection: newDirection, page: 0 });
        },
        [updateParams, defaultSortDirection]
    );

    const toggleSort = useCallback(
        (field: string) => {
            const currentSortBy = stateFromUrl.sortBy;
            const currentDirection = stateFromUrl.sortDirection;

            if (currentSortBy === field) {
                // Toggle direction if same field
                const newDirection: SortDirection = currentDirection === "asc" ? "desc" : "asc";
                updateParams({ sortDirection: newDirection, page: 0 });
            } else {
                // New field, use default direction
                updateParams({ sortBy: field, sortDirection: defaultSortDirection, page: 0 });
            }
        },
        [stateFromUrl.sortBy, stateFromUrl.sortDirection, updateParams, defaultSortDirection]
    );

    const setSearch = useCallback(
        (query: string) => {
            // Debounce URL update
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            debounceTimerRef.current = setTimeout(() => {
                updateParams({ search: query.trim(), page: 0 });
            }, searchDebounceMs);
        },
        [updateParams, searchDebounceMs]
    );

    const setSearchInput = useCallback(
        (query: string) => {
            setSearchInputState(query);
            setSearch(query);
        },
        [setSearch]
    );

    const reset = useCallback(() => {
        setSearchInputState("");
        const newParams = new URLSearchParams(location.search);
        newParams.delete(getParamName("page"));
        newParams.delete(getParamName("size"));
        newParams.delete(getParamName("sortBy"));
        newParams.delete(getParamName("sortDir"));
        newParams.delete(getParamName("search"));

        const newSearch = newParams.toString();
        history.replace({
            pathname: location.pathname,
            search: newSearch ? `?${newSearch}` : "",
        });
    }, [history, location.pathname, location.search, getParamName]);

    const isSorted = useCallback(
        (field: string) => stateFromUrl.sortBy === field,
        [stateFromUrl.sortBy]
    );

    const getSortDirection = useCallback(
        (field: string): SortDirection | undefined => {
            return stateFromUrl.sortBy === field ? stateFromUrl.sortDirection : undefined;
        },
        [stateFromUrl.sortBy, stateFromUrl.sortDirection]
    );

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    return {
        ...stateFromUrl,
        searchInput,
        setPage,
        setPageSize,
        setSort,
        toggleSort,
        setSearch,
        setSearchInput,
        reset,
        isSorted,
        getSortDirection,
    };
}

export default useListState;
